/**
 * Compound Interest Calculator - Core Calculations
 * 
 * Supports:
 * - Simple interest (Cont de economii)
 * - Compound interest (Unit Linked)
 * - Contribution pause periods
 * - Administrative fees
 */

/**
 * Calculate simple interest (for savings accounts)
 * Formula: I = P × r × t (interest is NOT reinvested)
 */
export const calculateSimpleInterest = ({
  initialDeposit = 0,
  monthlyContribution = 0,
  annualInterestRate = 0,
  years = 0,
  taxRate = 0,
  pauseStartYear = 0,
  pauseEndYear = 0,
}) => {
  const r = annualInterestRate / 100;
  const yearlyBreakdown = [];
  
  let totalContributions = initialDeposit;
  let totalInterestGross = 0;
  let totalTaxPaid = 0;
  let currentBalance = initialDeposit;
  
  for (let year = 1; year <= years; year++) {
    const yearStartBalance = currentBalance;
    
    // Check if we're in a pause period
    const isPaused = pauseStartYear > 0 && pauseEndYear > 0 && 
                     year >= pauseStartYear && year <= pauseEndYear;
    
    // Annual contribution (only if not paused)
    const yearContributions = isPaused ? 0 : monthlyContribution * 12;
    
    // Simple interest: calculated on the balance at start of year
    // Interest is calculated on principal only, not on accumulated interest
    const yearInterestGross = yearStartBalance * r;
    
    // Apply tax on interest
    const yearTaxPaid = yearInterestGross * (taxRate / 100);
    const yearInterestNet = yearInterestGross - yearTaxPaid;
    
    // Update balance: add contributions and net interest
    currentBalance = yearStartBalance + yearContributions + yearInterestNet;
    
    totalContributions += yearContributions;
    totalInterestGross += yearInterestGross;
    totalTaxPaid += yearTaxPaid;
    
    yearlyBreakdown.push({
      year,
      yearStartBalance: Math.round(yearStartBalance * 100) / 100,
      contributions: Math.round(yearContributions * 100) / 100,
      totalContributions: Math.round(totalContributions * 100) / 100,
      interestGross: Math.round(yearInterestGross * 100) / 100,
      taxPaid: Math.round(yearTaxPaid * 100) / 100,
      interestNet: Math.round(yearInterestNet * 100) / 100,
      endBalance: Math.round(currentBalance * 100) / 100,
      totalInterestGross: Math.round(totalInterestGross * 100) / 100,
      totalTaxPaid: Math.round(totalTaxPaid * 100) / 100,
      isPaused,
    });
  }
  
  const totalInterestNet = totalInterestGross - totalTaxPaid;
  
  return {
    finalValue: Math.round(currentBalance * 100) / 100,
    finalValueNet: Math.round(currentBalance * 100) / 100,
    totalContributions: Math.round(totalContributions * 100) / 100,
    totalInterestGross: Math.round(totalInterestGross * 100) / 100,
    totalInterestNet: Math.round(totalInterestNet * 100) / 100,
    totalTaxPaid: Math.round(totalTaxPaid * 100) / 100,
    yearlyBreakdown,
  };
};

/**
 * Calculate compound interest with regular contributions
 * Supports: pause periods, administrative fees
 */
export const calculateCompoundInterest = ({
  initialDeposit = 0,
  monthlyContribution = 0,
  annualInterestRate = 0,
  years = 0,
  compoundingFrequency = 12,
  contributionFrequency = 12,
  taxRate = 0,
  taxOnEachPeriod = false,
  adminFeeRate = 0, // Annual administrative fee (%)
  pauseStartYear = 0, // Year when contribution pause starts
  pauseEndYear = 0, // Year when contribution pause ends
  useSimpleInterest = false, // Use simple interest instead of compound
}) => {
  // If using simple interest, delegate to that function
  if (useSimpleInterest) {
    return calculateSimpleInterest({
      initialDeposit,
      monthlyContribution,
      annualInterestRate,
      years,
      taxRate,
      pauseStartYear,
      pauseEndYear,
    });
  }
  
  const r = annualInterestRate / 100;
  const n = compoundingFrequency;
  const periodicRate = r / n;
  const adminFee = adminFeeRate / 100;
  
  const yearlyBreakdown = [];
  let currentBalance = initialDeposit;
  let totalContributions = initialDeposit;
  let totalInterestGross = 0;
  let totalTaxPaid = 0;
  let totalAdminFees = 0;
  
  const annualContribution = monthlyContribution * 12;
  const contributionPerPeriod = annualContribution / n;
  
  for (let year = 1; year <= years; year++) {
    const yearStartBalance = currentBalance;
    let yearContributions = 0;
    let yearInterestGross = 0;
    let yearTaxPaid = 0;
    let yearAdminFee = 0;
    
    // Check if we're in a pause period
    const isPaused = pauseStartYear > 0 && pauseEndYear > 0 && 
                     year >= pauseStartYear && year <= pauseEndYear;
    
    // Calculate for each compounding period within the year
    for (let period = 1; period <= n; period++) {
      // Step 1: Calculate and apply interest
      const periodInterest = currentBalance * periodicRate;
      yearInterestGross += periodInterest;
      
      if (taxOnEachPeriod && taxRate > 0) {
        const periodTax = periodInterest * (taxRate / 100);
        yearTaxPaid += periodTax;
        currentBalance += periodInterest - periodTax;
      } else {
        currentBalance += periodInterest;
      }
      
      // Step 2: Add contribution (only if not paused)
      if (!isPaused) {
        currentBalance += contributionPerPeriod;
        yearContributions += contributionPerPeriod;
      }
    }
    
    // Apply annual administrative fee (Unit Linked)
    if (adminFee > 0) {
      yearAdminFee = currentBalance * adminFee;
      currentBalance -= yearAdminFee;
      totalAdminFees += yearAdminFee;
    }
    
    // If tax not applied each period, track for display
    if (!taxOnEachPeriod && taxRate > 0) {
      yearTaxPaid = yearInterestGross * (taxRate / 100);
    }
    
    totalContributions += yearContributions;
    totalInterestGross += yearInterestGross;
    totalTaxPaid += yearTaxPaid;
    
    yearlyBreakdown.push({
      year,
      yearStartBalance: Math.round(yearStartBalance * 100) / 100,
      contributions: Math.round(yearContributions * 100) / 100,
      totalContributions: Math.round(totalContributions * 100) / 100,
      interestGross: Math.round(yearInterestGross * 100) / 100,
      taxPaid: Math.round(yearTaxPaid * 100) / 100,
      interestNet: Math.round((yearInterestGross - yearTaxPaid) * 100) / 100,
      adminFee: Math.round(yearAdminFee * 100) / 100,
      endBalance: Math.round(currentBalance * 100) / 100,
      totalInterestGross: Math.round(totalInterestGross * 100) / 100,
      totalTaxPaid: Math.round(totalTaxPaid * 100) / 100,
      totalAdminFees: Math.round(totalAdminFees * 100) / 100,
      isPaused,
    });
  }
  
  let netFinalValue = currentBalance;
  if (!taxOnEachPeriod && taxRate > 0) {
    const totalTax = totalInterestGross * (taxRate / 100);
    netFinalValue = currentBalance - totalTax;
    totalTaxPaid = totalTax;
  }
  
  const totalInterestNet = totalInterestGross - totalTaxPaid;
  
  return {
    finalValue: Math.round(currentBalance * 100) / 100,
    finalValueNet: Math.round(netFinalValue * 100) / 100,
    totalContributions: Math.round(totalContributions * 100) / 100,
    totalInterestGross: Math.round(totalInterestGross * 100) / 100,
    totalInterestNet: Math.round(totalInterestNet * 100) / 100,
    totalTaxPaid: Math.round(totalTaxPaid * 100) / 100,
    totalAdminFees: Math.round(totalAdminFees * 100) / 100,
    yearlyBreakdown,
  };
};

/**
 * Compare two strategies (different contributions)
 */
export const compareStrategies = (strategy1Params, strategy2Params) => {
  const result1 = calculateCompoundInterest(strategy1Params);
  const result2 = calculateCompoundInterest(strategy2Params);
  
  return {
    strategy1: result1,
    strategy2: result2,
    difference: {
      finalValue: result1.finalValue - result2.finalValue,
      totalInterestNet: result1.totalInterestNet - result2.totalInterestNet,
      totalContributions: result1.totalContributions - result2.totalContributions,
    },
  };
};

/**
 * Generate chart data from yearly breakdown
 */
export const generateChartData = (yearlyBreakdown) => {
  return yearlyBreakdown.map((year) => ({
    year: year.year,
    contributions: year.totalContributions,
    interest: year.totalInterestGross - year.totalTaxPaid,
    total: year.endBalance,
    interestGross: year.totalInterestGross,
    taxPaid: year.totalTaxPaid,
    isPaused: year.isPaused,
  }));
};
